import { GET, PATCH, POST } from "@/api";
import { useAuthStore } from "@/context/auth-store/auth-store";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Flex,
  Tooltip,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import InviteMemberModal from "./components/invite-member-modal";
import { TrashBoxIcon } from "@/assets/icons/trash-box-icon";
import { CreateOrganizationModal } from "@/components/create-organization-modal";
import { RefreshBoxIcon } from "@/assets/icons/refresh-box-icon";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Pagination } from "@/components/pagination";
import { EditableInput } from "@/components/editable-input";
import { useTriggerStore } from "@/context/trigger-store/trigger-store";
import { useOrganization } from "@/hooks/use-organization";

const Members: FC = () => {
  const { organization, refetchUser, user } = useAuthStore((state) => ({
    organization: state.activeOrganization,
    setActiveOrg: state.setActiveOrganization,
    refetchUser: state.refetchUser,
    user: state.user,
  }));
  const triggerOrganization = useTriggerStore((s) => s.triggerOrganization);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { t } = useTranslation();
  const toast = useToast();

  const { data: OrganizationList } = useOrganization();
  const { data: membersData, refetch: membersDataRefetch } = useQuery(
    ["members", organization?.id],
    async () => {
      if (!organization) return;
      const { data } = await GET("/org/members/{org_pk}/", {
        params: {
          path: {
            org_pk: organization?.id.toString(),
          },
        },
      });
      return data;
    }
  );

  const { data: rolesData } = useQuery(
    ["roles", organization?.id],
    async () => {
      if (!organization) return;
      const { data } = await GET("/org/roles/{org_pk}/", {
        params: {
          path: {
            org_pk: organization?.id.toString(),
          },
        },
      });

      return data;
    }
  );

  const updateRoles = useCallback(
    async (
      organizationId: number | undefined,
      memberId: number,
      roleId: number
    ) => {
      if (!organizationId) return;
      const { data, response } = await PATCH("/roles/{org_pk}/change/", {
        params: {
          path: {
            org_pk: organizationId.toString(),
          },
        },
        body: {
          user: memberId,
          role: roleId,
        },
      });

      if (response.status === 403) {
        toast({
          status: "error",
          description: t("no_authorization_letter"),
          duration: 3000,
        });
        membersDataRefetch();
        return;
      }
      toast({
        status: "success",
        description: t("role_updated_successfully"),
        duration: 3000,
      });
      membersDataRefetch();
      return data;
    },
    [membersDataRefetch, t, toast]
  );

  const deleteMember = useCallback(
    async (memberId: number, organizationId: number) => {
      const { error, response } = await POST("/roles/{org_pk}/remove-user/", {
        params: {
          path: {
            org_pk: organizationId.toString(),
          },
        },
        body: {
          user: memberId,
        },
      });
      if (error) {
        console.error("deleteMember error", error);

        toast({
          status: "error",
          description: error.detail || t("transaction_failed"),
          duration: 3000,
        });

        return;
      }
      setTimeout(() => {
        membersDataRefetch();
      }, 1000);

      if (response.status === 200) {
        toast({
          status: "success",
          description: t("user_deleted_successfully"),
          duration: 3000,
        });

        return;
      }
      if (response.status === 400) {
        toast({
          status: "error",
          description: t("failed_to_delete_user"),
          duration: 3000,
        });

        return;
      }
      if (response.status === 403) {
        toast({
          status: "error",
          description: t("you_are_not_authorized_to_delete_users"),
          duration: 3000,
        });

        return;
      }

      toast({
        status: "error",
        description: t("transaction_failed"),
        duration: 3000,
      });
    },
    [membersDataRefetch, t, toast]
  );
  const membersPerPage = 5;

  const totalPages = membersData
    ? Math.ceil(membersData?.length / membersPerPage)
    : 0;
  const startIndex = (currentPage - 1) * membersPerPage;
  const endIndex = startIndex + membersPerPage;

  const currentPageMembers = membersData?.slice(startIndex, endIndex) ?? [];

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const updateOrganizationName = useCallback(
    async (name: string) => {
      if (!organization?.id) {
        return;
      }

      const { data, error } = await PATCH("/org/{id}/", {
        params: {
          path: {
            id: organization.id,
          },
        },
        body: {
          name,
        },
      });
      if (error) {
        toast({
          status: "error",
          description: error.detail || t("transaction_failed"),
          duration: 3000,
        });
        return;
      }

      if (data) {
        toast({
          status: "success",
          description: t("transaction_success"),
          duration: 3000,
        });

        triggerOrganization();
        refetchUser(true);
      }
    },
    [organization, refetchUser, t, toast, triggerOrganization]
  );

  return (
    <Flex flexDir="column" rowGap="20px" h="full" ml="24px">
      <Flex
        border="1px solid  #E1E3EA"
        p="3"
        alignItems="center"
        w="full"
        bg="white"
        shadow="sm"
        borderRadius="6px"
        h="110px"
      >
        <Flex w="100%" gap={2} alignItems="center" justifyContent="flex-start">
          <Text color="gray" fontSize="18px" fontWeight="500">
            {`${t("organization_name")}:`}
          </Text>
          {organization && (
            <EditableInput
              tooltipCondition={organization.owner !== user?.id}
              tooltipMessage={t("no_authorization_letter")}
              color="black.active"
              fontSize="18px"
              fontWeight="700"
              onSubmit={updateOrganizationName}
              isDisabled={organization.owner !== user?.id}
              key={organization.name + organization.id}
              defaultValue={
                organization.name
                  ? organization.name
                  : t("no_organization_found")
              }
            />
          )}
        </Flex>

        <Flex alignItems="center" justifyContent="flex-end" w="full" gap="5">
          <Flex>
            <CreateOrganizationModal />
          </Flex>

          <Flex>
            <Button
              onClick={() => onOpen()}
              fontSize="13px"
              bg="black.active"
              bgColor="blue.shiny"
              textColor="#fff"
              cursor="pointer"
              _hover={{
                opacity: 0.8,
              }}
              type="submit"
              borderRadius="3px"
              h="36px"
              w="202px"
            >
              {t("add_new_team_member")}
            </Button>
            <InviteMemberModal
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              OrganizationList={OrganizationList ?? []}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex
        px="4"
        pt="6"
        pb="8"
        w="full"
        borderRadius="6px"
        flexDir="column"
        gap="6"
        h="full"
        bgColor="white"
        overflowY="auto"
        shadow="0px 1px 1px rgba(0, 0, 0, 0.12)"
        border="1px solid  #E1E3EA"
      >
        <Box py="1" h="420px">
          <TableContainer justifyContent="space-between" alignItems="center">
            <Table variant="simple" justifyContent="space-between">
              <Thead>
                <Tr>
                  <Th borderWidth="0" textAlign="center" w="20%">
                    <Text
                      fontWeight="700"
                      fontSize="16px"
                      opacity="0.33"
                      textAlign="start"
                    >
                      {t("name")}
                    </Text>
                  </Th>
                  {/* <Th
                    borderWidth="0"
                    justifyContent={"center"}
                    alignItems={"center"}
                    w="20%"
                  >
                    <Text
                      w="10%"
                      fontWeight="700"
                      fontSize="16px"
                      opacity="0.33"
                    >
                      {t("status")}
                    </Text>
                  </Th> */}
                  <Th borderWidth="0" w="25%">
                    <Text fontWeight="700" fontSize="16px" opacity="0.33">
                      {t("roles")}
                    </Text>
                  </Th>
                  <Th borderWidth="0" w="30%">
                    <Text fontWeight="700" fontSize="16px" opacity="0.33">
                      {t("email")}
                    </Text>
                  </Th>
                  <Th borderWidth="0" w="15%"></Th>
                </Tr>
              </Thead>
              <Tbody mt="2">
                {Array.isArray(currentPageMembers) &&
                  currentPageMembers?.map((member) => (
                    <Tr
                      key={member.profile.id}
                      alignItems="center"
                      borderBottom="1px solid rgba(144, 123, 123, 0.08)"
                    >
                      <Td borderWidth="0">
                        <Tooltip
                          visibility={
                            `${member.profile.first_name} ${member.profile.last_name}`
                              .length > 20
                              ? "visible"
                              : "hidden"
                          }
                          shadow="0 0 10px rgba(0, 0, 0, 0.3)"
                          minW="140px"
                          fontWeight="500"
                          fontSize="16px"
                          bgColor="white"
                          p="6px"
                          color="black.active"
                          borderRadius="6px"
                          label={`${member.profile.first_name} ${member.profile.last_name}`}
                        >
                          <Flex gap={2} alignItems="center">
                            <Avatar
                              size="sm"
                              name={member.profile.first_name}
                              src={member.profile.picture ?? undefined}
                            />
                            <Text
                              cursor="pointer"
                              color="black.active"
                              fontSize="md"
                              textTransform="capitalize"
                              fontWeight="semibold"
                              lineHeight="16px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {`${member.profile.first_name} ${member.profile.last_name}`
                                .length > 20
                                ? `${`${member.profile.first_name} ${member.profile.last_name}`.slice(0, 20)}...`
                                : `${member.profile.first_name} ${member.profile.last_name}`}
                            </Text>
                          </Flex>
                        </Tooltip>
                      </Td>
                      {/* <Td borderWidth="0">
                        <StatusBadge
                          status={member.profile.status}
                          text={member.profile.statusText}
                        />
                      </Td> */}
                      <Td borderWidth="0" alignItems="center" cursor="pointer">
                        <Popover>
                          {({ isOpen, onClose }) => (
                            <>
                              {member.role === "Owner" ? (
                                <Box
                                  mr="34px"
                                  px="2"
                                  width="170px"
                                  h="36px"
                                  bg="white"
                                  border="1px solid #3B5998 "
                                  borderRadius="6px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-start"
                                >
                                  <Text
                                    textAlign="center"
                                    textOverflow="ellipsis"
                                    overflow="hidden"
                                    fontSize="14px"
                                    fontWeight="600"
                                    color="blue.main"
                                  >
                                    {t(
                                      `role_names.${member.role.toLowerCase()}`
                                    )}
                                  </Text>
                                </Box>
                              ) : (
                                <PopoverTrigger>
                                  <Button
                                    mr="34px"
                                    px="2"
                                    width="170px"
                                    h="36px"
                                    bg="white"
                                    border="1px solid #3B5998 "
                                    borderRadius="6px"
                                  >
                                    <Flex
                                      justifyContent="space-between"
                                      alignItems="center"
                                      w="full"
                                    >
                                      <Text
                                        textAlign="center"
                                        as="span"
                                        textOverflow="ellipsis"
                                        overflow="hidden"
                                        fontSize="14px"
                                        color="blue.main"
                                      >
                                        {t(
                                          `role_names.${member.role.toLowerCase()}`
                                        ) || t("select-role")}{" "}
                                      </Text>
                                      {Array.isArray(rolesData) &&
                                        rolesData.length > 1 &&
                                        (isOpen ? (
                                          <Box>
                                            <ChevronUpIcon color="blue.main" />
                                          </Box>
                                        ) : (
                                          <Box>
                                            <ChevronDownIcon color="blue.main" />
                                          </Box>
                                        ))}
                                    </Flex>
                                  </Button>
                                </PopoverTrigger>
                              )}
                              <PopoverContent
                                bg="white"
                                borderRadius="6px"
                                w="170px"
                                border={
                                  Array.isArray(rolesData) &&
                                  rolesData.length > 0
                                    ? "1px solid #3B5998"
                                    : "none"
                                }
                              >
                                {Array.isArray(rolesData) &&
                                  rolesData?.map((role, index) => (
                                    <React.Fragment key={role.id}>
                                      <PopoverBody
                                        _hover={{
                                          bg: "gray.soft",
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <Button
                                          variant="unstyled"
                                          textOverflow="ellipsis"
                                          overflow="hidden"
                                          fontSize="14px"
                                          textAlign={"left"}
                                          w="full"
                                          size={"none"}
                                          color="blue.main"
                                          fontWeight="600"
                                          key={index}
                                          onClick={() => {
                                            updateRoles(
                                              organization?.id,
                                              member.profile.id,
                                              role.id
                                            );
                                            onClose();
                                          }}
                                        >
                                          {t(
                                            `role_names.${role.title.toLowerCase()}`
                                          )}
                                        </Button>
                                      </PopoverBody>
                                      {index !== rolesData?.length - 1 && (
                                        <Divider />
                                      )}
                                    </React.Fragment>
                                  ))}
                              </PopoverContent>
                            </>
                          )}
                        </Popover>
                      </Td>
                      <Td borderWidth="0" alignItems="center" cursor="pointer">
                        {member.profile.email}
                      </Td>
                      <Td borderWidth="0">
                        <Flex
                          justifyContent="flex-end"
                          gap={4}
                          alignItems="center"
                        >
                          {/* <IconButton
                            cursor="pointer"
                            aria-label="remove-channel"
                            variant="unstyled"
                            onClick={onOpenAlert}
                          >
                            <RefreshBoxIcon width="33px" height="33px" />
                          </IconButton> */}
                          <DeleteAlert
                            channel={member.profile.email}
                            onDelete={() => {
                              if (organization?.id) {
                                deleteMember(
                                  member.profile.id,
                                  organization.id
                                );
                                return;
                              }
                              toast({
                                status: "error",
                                description: t("user-could-not-be-deleted"),
                                duration: 3000,
                              });
                            }}
                            trigger={(onOpen) => (
                              <Button
                                size="none"
                                cursor="pointer"
                                variant="unstyled"
                                onClick={onOpen}
                              >
                                <TrashBoxIcon width="33px" height="33px" />
                              </Button>
                            )}
                          />

                          {/* <AlertDialog
                            leastDestructiveRef={cancelRef}
                            onClose={onCloseAlert}
                            isOpen={isOpenAlert}
                            isCentered
                          >
                            <AlertDialogOverlay>
                              <AlertDialogContent bg="white.bg">
                                <AlertDialogHeader
                                  fontSize="lg"
                                  fontWeight="bold"
                                >
                                  You are deleting {member.profile.email}
                                </AlertDialogHeader>

                                <AlertDialogBody>
                                  Are you sure? You can't undo this action
                                  afterwards.
                                </AlertDialogBody>

                                <AlertDialogFooter>
                                  <Button onClick={onClose}>Cancel</Button>
                                  <Button
                                    colorScheme="red"
                                    onClick={() => {
                                      if (organization?.id) {
                                        deleteMember(
                                          member.profile.id,
                                          organization.id
                                        );
                                        return;
                                      }
                                      toast({
                                        status: "error",
                                        description: t(
                                          "user-could-not-be-deleted"
                                        ),
                                        duration: 3000,
                                      });
                                    }}
                                    ml={3}
                                  >
                                    Delete
                                  </Button>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialogOverlay>
                          </AlertDialog> */}
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Flex
          justifyContent="center"
          alignItems="center"
          position="relative"
          top="10"
        >
          {membersData?.length !== 0 && (
            <Box p={4}>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </Box>
          )}{" "}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const DeleteAlert: FC<{
  trigger: (onOpen: () => void) => React.ReactNode;
  onDelete?: () => void;
  channel: string;
}> = ({ trigger, onDelete, channel }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  return (
    <>
      {trigger(onOpen)}

      <AlertDialog
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="white.bg">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("alert.remove-modal.you-are-deleting-c")} {channel}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t("alert.remove-modal.are-you-sure")}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                onClick={onClose}
                bgColor="gray.light"
                width="80px"
                height="35px"
              >
                {t("alert.remove-modal.cancel")}
              </Button>
              <Button
                colorScheme="red"
                width="80px"
                height="35px"
                onClick={onDelete}
                ml={3}
              >
                {t("alert.remove-modal.delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
export default Members;
